type StreamingBufferStatus = 'waiting' | 'running' | 'complete';

export type StreamingBufferDrainCallback = (content: string) => unknown;

export type StreamingBufferConfig = {
  onComplete?: (content: string) => unknown;
  onFlush: (content: string) => unknown;
  chunkSize: number;
  flushIntervalMs: number;
};

export class StreamingBuffer {
  config: StreamingBufferConfig;

  content: string;

  pendingChunks: string[];

  flushTimeout: ReturnType<typeof setTimeout> | undefined;

  status: StreamingBufferStatus;

  constructor(config: StreamingBufferConfig) {
    this.config = config;
    this.content = '';
    this.pendingChunks = [];
    this.status = 'waiting';
  }

  get length() {
    const lengthOfPendingChunks = this.pendingChunks.reduce(
      (total, chunk) => total + chunk.length,
      0,
    );
    return this.content.length + lengthOfPendingChunks;
  }

  push(str: string) {
    const chunks = this.splitStringIntoChunks(str);
    this.pendingChunks.push(...chunks);
    if (this.status === 'waiting') {
      this.status = 'running';
      this.flush();
    }
  }

  complete() {
    this.status = 'complete';

    if (this.pendingChunks.length === 0) {
      this.config.onComplete?.(this.content);
    }
  }

  cancel() {
    this.status = 'complete';

    this.pendingChunks = [];
    clearTimeout(this.flushTimeout);
  }

  flush() {
    const chunk = this.pendingChunks.shift();

    if (chunk) {
      this.content += chunk;
      this.config.onFlush(this.content);
    }

    if (this.pendingChunks.length > 0) {
      this.flushTimeout = setTimeout(
        () => this.flush(),
        this.config.flushIntervalMs,
      );
    } else if (this.status === 'complete') {
      this.config.onComplete?.(this.content);
    } else {
      this.status = 'waiting';
    }
  }

  splitStringIntoChunks(str: string) {
    const chunks = [];
    for (let idx = 0; idx < str.length; idx += this.config.chunkSize) {
      const chunk = str.substring(idx, idx + this.config.chunkSize);
      chunks.push(chunk);
    }
    return chunks;
  }
}
